<template>
  <div
    :id="id"
    class="results-map" />
</template>

<script>
import { mapActions } from "vuex";
import d3 from "@/plugins/d3";

import MapMixin from "@/mixins/map";
import { debounce } from "@/helpers/utils";

const mixins = [MapMixin];

const props = {
  results: {
    type: Object,
    required: true,
  },
  geoType: {
    type: String,
    required: true,
  },
  showCities: {
    type: Boolean,
    required: false,
    default: false,
  },
};

const computed = {
  id() {
    return `results-map-${this.results.race_id}`;
  },
  state() {
    let state = this.results.state_name;
    if (this.results.office === "President" && state.includes("02")) {
      state = state.replace(" 02", "");
    }
    return state;
  },
  geojson() {
    let data;
    if (this.state === "Alaska") {
      data = this.topojson("state")();
    } else if (this.geoType === "vcu") {
      try {
        data = this.topojson("vcu", this.state)();
      } catch {
        this.setUiError(`No VCU shapefile for ${this.state}`);
      }
    } else {
      data = this.topojson(this.geoType)();
    }

    if (this.stateFips && this.stateFips !== "00" && !["vcu", "precinct"].includes(this.geoType)) {
      data.features = data.features.filter((item) => {
        if (this.stateFips === "02") {
          return item.properties.GEOID === this.stateFips;
        } else {
          return item.properties.STATEFP === this.stateFips;
        }
      });
    }
    return data;
  },
  projection() {
    return this.stateFips && this.stateFips !== "00" ? d3.geoMercator() : d3.geoAlbersUsa();
  },
};

const methods = {
  ...mapActions(["setUiError", "setGeoSelection"]),
  getSelectedResults(d) {
    let result = null;
    // reports at state level for Alaska
    if (this.stateFips === "02") {
      return this.results;
    }
    if (this.geoType === "county" && this.results.counties) {
      if (!this.results.counties) return null;
      result = this.results.counties.find(
        (item) => item.vcu.toLowerCase() === d.properties.NAME.toLowerCase()
      );
    }
    if (this.geoType === "vcu") {
      result = this.results.counties.find(
        (item) => item.vcu.toLowerCase() === d.properties.NAME.toLowerCase()
      );
    }
    if (this.geoType === "precinct") {
      result = this.results.precinctsData.find((item) => item.precinct === d.properties.Precinct);
    }
    if (typeof result === "undefined" && this.geoType !== "vcu") {
      // console.error(`${this.geoType} : ${d.properties.NAME} did not match`)
    }
    return result;
  },
  getFillColor(d) {
    const resultData = this.getSelectedResults(d);
    if (!resultData) {
      return "#e0e0e0";
    }
    let winner = resultData.candidates.find((c) => c.winning);
    if (winner) {
      return winner.color;
    }
    return "#bdbdbd";
  },
  pathClick(feature) {
    const self = this;
    const result = self.getSelectedResults(feature);

    if (!result) return;

    self.setGeoSelection(feature.properties.NAME);

    if (self.smAndDown) {
      self.highlightSelected(feature.properties.NAME, this, "NAME");
    }
  },
  selectionHighlighting() {
    const self = this;
    self.g.selectAll("path").each(function (d) {
      if (d.properties.NAME === self.geoSelection) {
        self.highlightSelected(self.geoSelection, this, "NAME");
      }
    });
  },
};

const watch = {
  theme() {
    this.drawMap();
  },
  geoType() {
    this.drawMap();
  },
  results(value, oldValue) {
    if (value.lastUpdated !== oldValue.lastUpdated) {
      this.drawMap();
    }
  },
  geoSelection() {
    this.drawMap();
  },
};

export default {
  mixins,
  props,
  data() {
    return {
      propKey: "NAME",
      config: {
        disableSvgClick: false,
        style: {
          opacity: 0.9,
          strokeWidth: () => {
            if (this.geoType === "county") {
              return "0.75px";
            } else {
              return "0.4px";
            }
          },
        },
      },
    };
  },
  computed,
  watch,
  mounted() {
    this.drawMap();

    window.addEventListener(
      "resize",
      debounce(() => {
        this.drawMap();
      }),
      1000
    );
  },
  beforeUnmount() {
    window.removeEventListener(
      "resize",
      debounce(() => {
        this.drawMap();
      }),
      1000
    );

    this.hideTooltip();
  },
  methods,
};
</script>
